import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import confetti from 'canvas-confetti';

gsap.registerPlugin(ScrollTrigger);

export function initScrollAndInteractions(scene3D, dayCharactersManager, musicPlayer) {
  // 1. Initialize Lenis Smooth Scrolling
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: 'vertical',
    smoothWheel: true,
    touchMultiplier: 1.5
  });

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  // Sync Lenis with GSAP ScrollTrigger
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);

  // 2. Hero Section Pinned Timeline (Avatar Reveal Choreography)
  const heroSection = document.getElementById('hero');
  const heroContent = document.getElementById('hero-content');
  const knockoutOverlay = document.getElementById('knockout-mask-overlay');

  if (heroSection) {
    const heroTl = gsap.timeline({
      scrollTrigger: {
        trigger: heroSection,
        start: 'top top',
        end: '+=180%',
        pin: true,
        scrub: 1.0,
        onUpdate: (self) => {
          if (scene3D) {
            scene3D.setScrollProgress(self.progress);
          }
        }
      }
    });

    // Step 1: Hero typography floats up & fades
    heroTl.to(heroContent, {
      y: -120,
      opacity: 0,
      duration: 0.35,
      ease: 'power2.inOut'
    }, 0);

    // Step 2: Knockout Romantic Banner reveals at peak avatar emergence
    if (knockoutOverlay) {
      heroTl.fromTo(knockoutOverlay, {
        opacity: 0,
        scale: 0.92
      }, {
        opacity: 1,
        scale: 1,
        duration: 0.45,
        ease: 'power2.out'
      }, 0.55);
    }
  }

  // 3. Day 1: Rose Day Pinned Scroll Timeline (Walk & Rose Handover)
  const roseSection = document.getElementById('day1-rose');
  if (roseSection) {
    gsap.timeline({
      scrollTrigger: {
        trigger: roseSection,
        start: 'top top',
        end: '+=150%',
        pin: true,
        scrub: 1.0,
        onUpdate: (self) => {
          if (dayCharactersManager) {
            dayCharactersManager.setRoseDayProgress(self.progress);
          }
        }
      }
    });
  }

  // 3b. Day 4: Teddy Day Scroll Kinematics (Boy in Teddy Suit walks and proposes)
  const teddySection = document.getElementById('day4-teddy');
  if (teddySection) {
    gsap.timeline({
      scrollTrigger: {
        trigger: teddySection,
        start: 'top top',
        end: '+=150%',
        pin: true,
        scrub: 1.0,
        onUpdate: (self) => {
          if (dayCharactersManager) {
            dayCharactersManager.setTeddyDayProgress(self.progress);
          }
        }
      }
    });
  }

  // 3c. Day 6: Hug & Kiss Day Scroll Kinematics (Boy rides bike, hops off, hugs & kisses girl)
  const hugSection = document.getElementById('day6-intimacy');
  if (hugSection) {
    gsap.timeline({
      scrollTrigger: {
        trigger: hugSection,
        start: 'top top',
        end: '+=150%',
        pin: true,
        scrub: 1.0,
        onUpdate: (self) => {
          if (dayCharactersManager) {
            dayCharactersManager.setHugDayProgress(self.progress);
          }
        }
      }
    });
  }

  // 4. Day 1: Rose Day Petals Selector
  const petalBtns = document.querySelectorAll('.petal-choice-btn');
  const roseQuoteDisplay = document.getElementById('rose-quote-display');
  petalBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      petalBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const quote = btn.getAttribute('data-quote');
      if (roseQuoteDisplay && quote) {
        gsap.to(roseQuoteDisplay, {
          opacity: 0,
          duration: 0.15,
          onComplete: () => {
            roseQuoteDisplay.textContent = quote;
            gsap.to(roseQuoteDisplay, { opacity: 1, duration: 0.3 });
          }
        });

        // Soft petal confetti
        const rect = btn.getBoundingClientRect();
        confetti({
          particleCount: 20,
          spread: 40,
          origin: {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight
          },
          colors: ['#FF4D6D', '#FFCCD5', '#E5B869']
        });
      }
    });
  });

  // 4. Day 2: Propose Day Velvet Ring Box Sparkle
  const ringBoxBtn = document.getElementById('ring-box-btn');
  if (ringBoxBtn) {
    ringBoxBtn.addEventListener('click', () => {
      gsap.fromTo(ringBoxBtn, { scale: 0.9, rotate: -8 }, { scale: 1.15, rotate: 6, duration: 0.3, yoyo: true, repeat: 1 });

      const rect = ringBoxBtn.getBoundingClientRect();
      confetti({
        particleCount: 45,
        spread: 70,
        origin: {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight
        },
        colors: ['#D4AF37', '#FFF', '#F5E6CC']
      });
    });
  }

  // 5. Day 3: Romantic Chocolate Chat Cards Tap & Sparkle
  const chocCards = document.querySelectorAll('.chocolate-chat-card');
  chocCards.forEach((card) => {
    card.addEventListener('click', () => {
      gsap.fromTo(card, { scale: 0.96 }, { scale: 1.02, duration: 0.25, yoyo: true, repeat: 1 });

      const rect = card.getBoundingClientRect();
      confetti({
        particleCount: 25,
        spread: 45,
        origin: {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight
        },
        colors: ['#D4AF37', '#8E1616', '#FAF0E6', '#FFCCD5']
      });
    });
  });

  // 6. Day 4: Teddy Day Hug-O-Meter
  const sendHugBtn = document.getElementById('btn-send-hug');
  const hugFillBar = document.getElementById('hug-fill-bar');
  const hugStatusText = document.getElementById('hug-status-text');
  let hugLevel = 50;

  if (sendHugBtn && hugFillBar && hugStatusText) {
    sendHugBtn.addEventListener('click', () => {
      hugLevel = Math.min(100, hugLevel + 25);
      hugFillBar.style.width = `${hugLevel}%`;

      if (hugLevel >= 100) {
        hugStatusText.textContent = 'Comfort Level: 100% MAXIMUM WARMTH & COZINESS! 💖🧸';
        sendHugBtn.textContent = 'Infinite Hugs Sent! 💕';

        confetti({
          particleCount: 50,
          spread: 60,
          colors: ['#FF8FA3', '#FF4D6D', '#FFE5D9']
        });
      } else {
        hugStatusText.textContent = `Comfort Level: ${hugLevel}% Cozy & Loved 💕`;
      }
    });
  }

  // 7. Day 7: The Big Proposal Climax & Souvenir Modal
  const yesBtn = document.getElementById('btn-valentine-yes');
  const maybeBtn = document.getElementById('btn-valentine-maybe');
  const souvenirModal = document.getElementById('souvenir-modal');
  const closeSouvenirBtn = document.getElementById('close-souvenir-btn');

  const playfulPhrases = [
    "Are you sure? 🥺",
    "Think again! 😉",
    "Nice try! 😜",
    "Error 404: NO not found! 💖",
    "Cupid says YES! 🏹",
    "Only YES allowed! 💍",
    "Yes of course! ❤️"
  ];
  let phraseIdx = 0;
  let dodgeCount = 0;

  if (yesBtn) {
    yesBtn.addEventListener('click', () => {
      if (scene3D) scene3D.triggerCelebration();
      if (dayCharactersManager) dayCharactersManager.triggerCelebration();

      // Confetti & Stardust Fireworks Blast
      const duration = 4.5 * 1000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 7,
          angle: 60,
          spread: 65,
          origin: { x: 0, y: 0.7 },
          colors: ['#FF2D55', '#FF758F', '#FFCCD5', '#E5B869', '#FFD700']
        });
        confetti({
          particleCount: 7,
          angle: 120,
          spread: 65,
          origin: { x: 1, y: 0.7 },
          colors: ['#FF2D55', '#FF758F', '#FFCCD5', '#E5B869', '#FFD700']
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();

      // Spawn floating hearts
      for (let i = 0; i < 20; i++) {
        setTimeout(() => {
          const heart = document.createElement('div');
          heart.className = 'floating-kiss-emoji';
          heart.textContent = ['💖', '💍', '✨', '🌹', '🥰'][Math.floor(Math.random() * 5)];
          heart.style.left = `${15 + Math.random() * 70}vw`;
          heart.style.top = `${60 + Math.random() * 20}vh`;
          heart.style.fontSize = `${1.8 + Math.random() * 1.5}rem`;
          document.body.appendChild(heart);
          setTimeout(() => heart.remove(), 1800);
        }, i * 120);
      }

      if (souvenirModal) {
        setTimeout(() => {
          souvenirModal.classList.add('is-active');
        }, 1200);
      }
    });
  }

  if (maybeBtn) {
    maybeBtn.addEventListener('click', () => {
      // Sweetly convert to YES!
      if (yesBtn) yesBtn.click();
    });
  }

  if (closeSouvenirBtn && souvenirModal) {
    closeSouvenirBtn.addEventListener('click', () => {
      souvenirModal.classList.remove('is-active');
    });
  }

  // 8. Ambient Music Button Toggle
  const musicToggleBtn = document.getElementById('music-toggle-btn');
  const eqBars = document.querySelectorAll('.eq-bar');

  if (musicToggleBtn && musicPlayer) {
    musicToggleBtn.addEventListener('click', () => {
      const isPlaying = musicPlayer.toggle();
      musicToggleBtn.classList.toggle('is-playing', isPlaying);
      eqBars.forEach(bar => {
        bar.classList.toggle('animating', isPlaying);
      });
    });
  }

  // 9. Download Certificate Feature
  const printBtn = document.getElementById('print-certificate-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }
}
